import { useCallback, useEffect, useRef, useState } from "react";
import { beaconCompleteRecording } from "../Requests/QuestionnaireRequests";
import useChunkedRecorderUpload from "./useChunkedRecorderUpload";

/**
 * Screen/camera recording for questionnaire respondents, built to survive a
 * multi-hour session.
 *
 * The design constraint that drives everything here: MediaRecorder without a
 * timeslice buffers the entire recording in the tab and hands it over as one
 * Blob at stop(). For a five-hour session that is gigabytes of memory followed
 * by a single unresumable upload — one dropped connection loses the lot.
 *
 * The chunked upload that solves it now lives in useChunkedRecorderUpload,
 * shared with voice answers. What stays here is stream acquisition, bitrates,
 * the session cap and the closing-tab beacon.
 */

/**
 * Pinned bitrate. Unconstrained screen capture runs 2.5-8 Mbps, which is
 * 5-18 GB for a five-hour session — a storage bill problem long before it is a
 * technical one. 1 Mbps is ample for screen content and puts a 10s chunk at
 * roughly 1.3 MB.
 */
const SCREEN_BITS_PER_SECOND = 1_000_000;
const CAMERA_BITS_PER_SECOND = 300_000;
const AUDIO_BITS_PER_SECOND = 64_000;

/** Hard session cap, so an abandoned tab cannot record indefinitely. */
const MAX_RECORDING_MS = 5 * 60 * 60 * 1000;

const canScreenRecord =
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;

function pickMimeType() {
    if (typeof MediaRecorder === "undefined") return "";
    const candidates = [
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp8",
        "video/webm",
        "video/mp4",
    ];
    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

export default function useQuestionnaireRecording() {
    const [screenEnabled, setScreenEnabled] = useState(false);
    const [cameraEnabled, setCameraEnabled] = useState(false);
    const [status, setStatus] = useState("idle"); // idle|recording|degraded|stopped
    const [error, setError] = useState(null);

    // One entry per stream. Each holds its own recorder, queue and cursor;
    // nothing here ever holds recorded data beyond what is queued for upload.
    const tracksRef = useRef({});
    const responseUuidRef = useRef(null);

    const { beginTrack, finishTrack, stopTrack } = useChunkedRecorderUpload({
        onDegraded: (message) => {
            setStatus("degraded");
            if (message) setError(message);
        },
    });

    const beginStream = useCallback(async (kind, stream) => {
        const mimeType = pickMimeType();

        const entry = await beginTrack(responseUuidRef.current, kind, stream, {
            mimeType: mimeType || undefined,
            videoBitsPerSecond: kind === "screen" ? SCREEN_BITS_PER_SECOND : CAMERA_BITS_PER_SECOND,
            audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
        });

        if (!entry) return false;

        // The user can revoke screen sharing from the browser's own UI at any
        // point. Treat it as a clean finish rather than an error.
        stream.getTracks().forEach((track) => {
            track.onended = () => stopTrack(entry, { finalize: true });
        });

        entry.cap = setTimeout(() => stopTrack(entry, { finalize: true }), MAX_RECORDING_MS);

        tracksRef.current[kind] = entry;
        return true;
    }, [beginTrack, stopTrack]);

    /**
     * Request permissions and start. Called only after informed consent has been
     * signed — the consent text is what discloses the recording.
     */
    const start = useCallback(async (responseUuid, { wantScreen, wantCamera }) => {
        responseUuidRef.current = responseUuid;
        let anyStarted = false;

        if (wantScreen && canScreenRecord) {
            try {
                const stream = await navigator.mediaDevices.getDisplayMedia({
                    video: { frameRate: 5 },
                    audio: false,
                });
                const ok = await beginStream("screen", stream);
                setScreenEnabled(ok);
                anyStarted = anyStarted || ok;
            } catch (e) {
                setScreenEnabled(false);
            }
        }

        if (wantCamera) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
                    audio: false,
                });
                const ok = await beginStream("camera", stream);
                setCameraEnabled(ok);
                anyStarted = anyStarted || ok;
            } catch (e) {
                setCameraEnabled(false);
            }
        }

        setStatus(anyStarted ? "recording" : "idle");
        return anyStarted;
    }, [beginStream]);

    const stop = useCallback(async () => {
        const entries = Object.values(tracksRef.current);
        tracksRef.current = {};

        entries.forEach((entry) => clearTimeout(entry.cap));

        await Promise.all(entries.map((entry) => finishTrack(entry)));

        setStatus("stopped");
        setScreenEnabled(false);
        setCameraEnabled(false);
    }, [finishTrack]);

    // A closing tab cannot await anything, so flush what we can and let the
    // beacon mark the recording complete. Whatever is already uploaded is kept.
    useEffect(() => {
        const handlePageHide = () => {
            Object.values(tracksRef.current).forEach((entry) => {
                try {
                    if (entry.recorder && entry.recorder.state === "recording") {
                        entry.recorder.requestData();
                        entry.recorder.stop();
                    }
                } catch (e) {
                    /* nothing useful to do while unloading */
                }
                if (entry.recordingUuid) {
                    beaconCompleteRecording(entry.recordingUuid);
                }
            });
        };

        window.addEventListener("pagehide", handlePageHide);
        return () => {
            window.removeEventListener("pagehide", handlePageHide);
            Object.values(tracksRef.current).forEach((entry) => {
                clearTimeout(entry.cap);
                stopTrack(entry, { finalize: false });
            });
            tracksRef.current = {};
        };
    }, [stopTrack]);

    return {
        start,
        stop,
        status,
        error,
        screenEnabled,
        cameraEnabled,
        canScreenRecord,
        cameraStream: () => tracksRef.current.camera?.stream || null,
    };
}
